/// <amd-module name="ServiceHooks/Web/Admin/Components/SubscriptionTable/SubscriptionTable" />
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { Card } from "VSSUI/Card";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Pill, PillSize } from "VSSUI/Pill";
import { Status, StatusSize } from "VSSUI/Status";
import {
    ColumnMore,
    ColumnSelect,
    ColumnSorting,
    ITableColumn,
    SimpleTableCell,
    SortOrder,
    Table,
    TableColumnLayout,
    TwoLineTableCell,
    renderLoadingCell,
} from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { VssPersona } from "VSSUI/VssPersona";
import * as React from "react";
import { Subscription, SubscriptionStatus } from "../../Contracts/ServiceHooks";
import {
    getConsumerActionForSubscriptionAsString,
    getConsumerForSubscriptionAsString,
    getEventTypeForSubscriptionAsString,
    getStatusForNotificationResult,
    getSubscriptionStatusAsString
} from "../../Utilities";
import { ISubscriptionTableProps } from "./SubscriptionTable.Props";

function getColumns(tableProps: ISubscriptionTableProps): ITableColumn<Subscription>[] {
    const {
        consumersById,
        eventTypesById,
        consumerActionsById,
        notificationSummaries,
        onUpdateSubscriptionStatus,
        onSubscriptionEdit,
        onSubscriptionDeleted,
        onRowActivated,
        canEditSubscriptions
    } = tableProps;

    const onSize = (event: MouseEvent, index: number, width: number) => {
        (columns[index].width as ObservableValue<number>).value = width;
    };

    const columns: ITableColumn<Subscription>[] = [
        
        new ColumnSelect(),
        {
            id: "consumer",
            name: Resources.DataConsumerName,
            onSize: onSize,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Subscription>, tableItem: Subscription) => {
                return (
                    <SimpleTableCell
                        key={"col-" + columnIndex}
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                    >
                        <div 
                            className="font-weight-semibold font-size-m margin-horizontal-8"
                        >
                            {getConsumerForSubscriptionAsString(consumersById, tableItem)}
                        </div>
                        {
                            tableItem.status !== SubscriptionStatus.Enabled && 
                                <Pill size={PillSize.compact}>
                                    {Resources.DisabledPillText}   
                                </Pill>
                        }
                    
                    </SimpleTableCell>
                );
            },
            width: new ObservableValue(-20)
        },
        {
            id: "event",
            name: Resources.DataEventName,
            onSize: onSize,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Subscription>, tableItem: Subscription) => {
                return (
                    <TwoLineTableCell
                        key={"col-" + columnIndex}
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                        line1={
                            <Tooltip overflowOnly={true}>
                                <div className="font-size-m text-ellipsis">
                                    {getEventTypeForSubscriptionAsString(eventTypesById, tableItem)}
                                </div>
                            </Tooltip>
                        }
                        line2={
                            <Tooltip overflowOnly={true}>
                                <div className="font-size-ms secondary-text">
                                    {tableItem.eventDescription}
                                </div>    
                            </Tooltip>
                        }
                    />
                )
            },
            width: new ObservableValue(-20)
        },
        {
            id: "action",
            name: Resources.DataActionName,
            onSize: onSize,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Subscription>, tableItem: Subscription) => {
                return (
                    <TwoLineTableCell
                        key={"col-" + columnIndex}
                        columnIndex={columnIndex}
                        tableColumn={tableColumn}
                        line1={
                            <Tooltip overflowOnly={true}>
                                <div className="font-size-m">
                                    {getConsumerActionForSubscriptionAsString(consumerActionsById, tableItem)}
                                </div>
                            </Tooltip>
                        }
                        line2={
                            <Tooltip overflowOnly={true}>
                                <div className="font-size-ms secondary-text">
                                    {tableItem.actionDescription}
                                </div>    
                            </Tooltip>
                        }
                    />
                )
            },
            width: new ObservableValue(-20)
        },
        {
            id: "owner",
            name: Resources.DataOwnerName,
            onSize: onSize,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Subscription>, tableItem: Subscription) => {
                return (
                    <SimpleTableCell
                        className="flex-row flex-center text-ellipsis"
                        columnIndex={columnIndex}
                        key={columnIndex}
                        tableColumn={tableColumn}
                    >
                        {tableItem.createdBy ? (
                            <>
                                <VssPersona
                                    className="icon-margin"
                                    displayName={tableItem.createdBy.displayName}
                                    imageUrl={tableItem.createdBy.imageUrl}
                                    size={"extra-small-plus"}
                                />
                                <Tooltip overflowOnly={true}>
                                    <div className="body-m text-ellipsis">{tableItem.createdBy.displayName}</div>
                                </Tooltip>
                            </>
                        ) : (
                            <Tooltip overflowOnly={true}>
                                <div className="body-m text-ellipsis">{Resources.Unknown}</div>
                            </Tooltip>
                        )}
                    </SimpleTableCell>
                );
            },
            width: new ObservableValue(-20)
        },
        {
            id: "status",
            name: Resources.Table7DayStatusName,
            onSize: onSize,
            renderCell: (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<Subscription>, tableItem: Subscription) => (
                <SimpleTableCell columnIndex={columnIndex} key={columnIndex} tableColumn={tableColumn}>
                    <Observer notificationSummaries={notificationSummaries}>
                        {() => {
                            const summary = notificationSummaries.get(tableItem.id);
                            return summary !== undefined ? (
                                summary.results.length > 0 ? (
                                    <div className="flex-row flex-center text-ellipsis">
                                        {summary.results.map((result, index) => (
                                            <React.Fragment key={index}>
                                                <Status
                                                    {...getStatusForNotificationResult(result.result)}
                                                    className="hooks-status-icon icon-margin"
                                                    size={StatusSize.m}
                                                />
                                                {result.notificationCount}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                ) : (
                                    <Tooltip overflowOnly={true}>
                                        <div className="text-ellipsis">{Resources.Table7DayStatusNone}</div>
                                    </Tooltip>
                                )
                            ) : (
                                renderLoadingCell(TableColumnLayout.singleLinePrefix)
                            );
                        }}
                    </Observer>
                </SimpleTableCell>
            ),
            width: new ObservableValue(-11)
        },
        new ColumnMore((subscription: Subscription) => {
            const status = subscription.status;

            const menuItems: IMenuItem[] = [
                {
                    id: "view-history",
                    text: Resources.TableMenuHistory,
                    onActivate: (event, item) => onRowActivated && onRowActivated(subscription)
                },
                {
                    id: "change-status",
                    text: status === SubscriptionStatus.Enabled ? Resources.TableMenuDisable : Resources.TableMenuEnable,
                    onActivate:
                        status === SubscriptionStatus.Enabled
                            ? () => {
                                  onUpdateSubscriptionStatus(SubscriptionStatus.DisabledByUser, subscription);
                              }
                            : () => {
                                  onUpdateSubscriptionStatus(SubscriptionStatus.Enabled, subscription);
                              }
                }
            ];

            if (canEditSubscriptions) {
                menuItems.push(
                    {
                        id: "edit",
                        text: Resources.TableMenuEdit,
                        onActivate: onSubscriptionEdit.bind(null, { ...subscription })
                    },
                    {
                        id: "delete",
                        text: Resources.TableMenuDelete,
                        onActivate: onSubscriptionDeleted.bind(null, subscription)
                    }
                );
            }

            return {
                id: "table-action-menu",
                items: menuItems
            };
        })
    ];

    return columns;
}

function getSortFunctions(tableProps: ISubscriptionTableProps): (((itemOne: Subscription, itemTwo: Subscription) => number) | null)[] {
    const { consumersById, consumerActionsById, eventTypesById } = tableProps;

    return [
        // Select
        null,
        // Consumer
        (itemOne: Subscription, itemTwo: Subscription): number => {
            return getConsumerForSubscriptionAsString(consumersById, itemOne).localeCompare(
                getConsumerForSubscriptionAsString(consumersById, itemTwo), navigator.language
            );
        },
        // Event
        (itemOne: Subscription, itemTwo: Subscription): number => {
            return getEventTypeForSubscriptionAsString(eventTypesById, itemOne).localeCompare(
                getEventTypeForSubscriptionAsString(eventTypesById, itemTwo), navigator.language
            );
        },
        // Event description
        null,
        // Action
        (itemOne: Subscription, itemTwo: Subscription): number => {
            return getConsumerActionForSubscriptionAsString(consumerActionsById, itemOne).localeCompare(
                getConsumerActionForSubscriptionAsString(consumerActionsById, itemTwo), navigator.language
            );
        },
        // Action Description
        null,
        // Owner
        (itemOne: Subscription, itemTwo: Subscription): number => {
            if (!itemOne.createdBy) {
                return -1;
            } else if (!itemTwo.createdBy) {
                return 1;
            }

            return itemOne.createdBy.displayName.localeCompare(itemTwo.createdBy.displayName, navigator.language);
        },
        // 7 Day State
        null,
        // State
        (itemOne: Subscription, itemTwo: Subscription): number => {
            return getSubscriptionStatusAsString(itemOne.status).localeCompare(getSubscriptionStatusAsString(itemTwo.status), navigator.language);
        }
    ];
}

export const SubscriptionTable: React.FunctionComponent<ISubscriptionTableProps> = props => {
    const { subscriptions, onRowActivated, onSort, selection, headerCommandBarItems } = props;
    // These get stored in state so we can use an arrow method to only compute their values once
    const [tableColumns] = React.useState<ITableColumn<Subscription>[]>(() => getColumns(props));
    const [sortFunctions] = React.useState(() => getSortFunctions(props));
    const [sortingBehavior] = React.useState(
        () =>
            new ColumnSorting<Subscription>((columnIndex: number, proposedSortOrder: SortOrder) => {
                onSort(columnIndex, proposedSortOrder, sortFunctions, tableColumns);
            })
    );

    return (
        <Card 
            className={"subscription-table bolt-table-card"} 
            contentProps={{ contentPadding: false }}
            titleProps={{ 
                text: Resources.TableTitle,
                ariaLevel: 3,
                className: "font-weight-semibold"
            }}
            headerCommandBarItems={headerCommandBarItems}
        >
            <Table
                behaviors={[sortingBehavior]}
                columns={tableColumns}
                itemProvider={subscriptions}
                onActivate={(event, item) => onRowActivated && onRowActivated(item.data)}
                selection={selection}
            />
        </Card>
    );
};
